/**
 * The server's clock, as far as this browser can tell it from the answers it gets —
 * the twin of `packages/limuzin_core/lib/src/server_clock.dart`, so that both
 * passenger apps and the driver's count a ride by the same clock.
 *
 * The meter is measured between moments the server wrote down — the passenger
 * aboard, the car at the kerb, a stop begun — and the fare ticking on this page
 * counts on from them to now. Counted to the computer's own now, a clock a few
 * seconds out shows a fare a step or two off what the driver's screen shows and
 * what the receipt will say. So anything held against a time the server wrote is
 * held against {@link ServerClock.now} instead.
 *
 * Every answer's `Date` says what time it was on the server, to the second and
 * rounded down, at some moment between the request going out and the answer coming
 * back. Each so bounds how far the server's clock is from this one, and the bounds
 * of answer after answer are narrowed together — to well under a second within a
 * few requests. A clock set while the page is open gives bounds the old ones do not
 * meet, and the reckoning starts again from those.
 *
 * Before any answer it is the computer's own clock, as everything was before.
 */
export class ServerClock {
  /** How far the server's clock may be ahead of this one, in ms; null before any answer. */
  private low: number | null = null;
  private high: number | null = null;

  constructor(private readonly local: () => number = Date.now) {}

  /** The server's clock less this one: the middle of what the answers so far allow. */
  get offset(): number {
    return this.low === null || this.high === null ? 0 : Math.round((this.low + this.high) / 2);
  }

  /** The time by the server's clock, in ms since the epoch. */
  now(): number {
    return this.local() + this.offset;
  }

  /**
   * An answer stamped [stamped] — its `Date`, whole seconds — after the request
   * went out at [sent] and before it was back at [received], both by this clock.
   */
  observe(sent: number, received: number, stamped: number): void {
    // The server's clock read [stamped] plus at most a second, at a moment this
    // one read between [sent] and [received].
    const low = stamped - received;
    const high = stamped + 1000 - sent;
    if (high < low) return;
    if (this.low === null || this.high === null || low > this.high || high < this.low) {
      this.low = low;
      this.high = high;
      return;
    }
    this.low = Math.max(this.low, low);
    this.high = Math.min(this.high, high);
  }

  /** What [response] said the time was, if it said. */
  observeResponse(response: Response, sent: number, received: number): void {
    const date = response.headers?.get('date');
    if (!date) return;
    const stamped = Date.parse(date);
    if (Number.isNaN(stamped)) return;
    this.observe(sent, received, stamped);
  }
}

/** The one the passenger's page reads, fed by every answer {@link Api} gets. */
export const serverClock = new ServerClock();
